/**
 * Live captions on the web and desktop (CallEngine.setTranscription / onSpeech / transcriptionCapability). The view
 * model picks the mode per device (core logic/transcription, docs/specs/on-device-transcription.md):
 * - "client" (the default): Desert Ant Voz in the browser (WebGPU, a 409 MB bundle downloaded from Hugging Face the
 *   first time someone turns captions on, then cached). Phrases are cut on silence and each one is transcribed whole;
 *   the text goes out with Voz's word times (80 ms steps). Where there's no WebGPU, the browser's Web Speech recognizer,
 *   but only when it runs on the device (`processLocally`, its language pack already installed): Chrome otherwise
 *   sends the audio to Google. No audio leaves the device in this mode.
 * - "server" (the fallback while Voz downloads, without an on-device engine, or after two failures): short clips of my
 *   processed microphone (MediaRecorder, restarted every CAPTION_CLIP_MS so each clip is a complete file), sent only
 *   when they were loud enough (core clipHasSpeech); the API transcribes them with Workers AI.
 */
import type { HuddleTranscription } from "@bundle/contracts";
import { Huddles, Transcription, type ModelState, type SpeechChunk, type TranscriptionCapability } from "@bundle/core";

type Recognition = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  processLocally?: boolean;
  onresult: ((e: { resultIndex: number; results: ArrayLike<{ isFinal: boolean; 0: { transcript: string } }> }) => void) | null;
  onend: (() => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  start(): void;
  stop(): void;
};

type RecognitionCtor = (new () => Recognition) & {
  available?(options: { langs: string[]; processLocally: boolean; quality?: string }): Promise<string>;
};

function recognitionCtor(): RecognitionCtor | undefined {
  const w = globalThis as unknown as { SpeechRecognition?: RecognitionCtor; webkitSpeechRecognition?: RecognitionCtor };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition;
}

const CLIP_TYPES = ["audio/webm;codecs=opus", "audio/ogg;codecs=opus", "audio/mp4", "audio/webm"];

/** Voz's languages (desert-ant-core 4.2.1 `Voz.supportedLanguages`). */
export const VOZ_LANGUAGES = ["bg", "cs", "da", "de", "el", "en", "es", "et", "fi", "fr", "hr", "hu", "it", "lt", "lv", "mt", "nl", "pl", "pt", "ro", "ru", "sk", "sl", "sv", "uk"];
/** The browser bundle's size (Desert Ant docs) and the memory a loaded instance takes. */
export const VOZ_BYTES = 409_000_000;
export const VOZ_MEMORY = 1_200_000_000;

/** Settings › Huddles "Make captions on this device" (this browser only; default on). */
const ON_DEVICE_KEY = "bundle.huddle.captionsOnDevice";
const ON_DEVICE_EVENT = "bundle:captions-on-device";

export function captionsOnDevice(): boolean {
  try {
    return localStorage.getItem(ON_DEVICE_KEY) !== "0";
  } catch {
    return true;
  }
}

export function setCaptionsOnDevice(on: boolean): void {
  try {
    localStorage.setItem(ON_DEVICE_KEY, on ? "1" : "0");
  } catch {
    // storage blocked: the setting lasts for this page
  }
  window.dispatchEvent(new Event(ON_DEVICE_EVENT));
}

export function captionsSupported(): boolean {
  return typeof MediaRecorder !== "undefined" || recognitionCtor() !== undefined;
}

/** The caption language: the app's, else the browser's. */
function captionLocale(): string {
  return (typeof document !== "undefined" && document.documentElement.lang) || (typeof navigator !== "undefined" ? navigator.language : "") || "en";
}

/** Silence that ends a phrase, the longest phrase, and audio kept from before the first loud chunk. */
export const PHRASE_SILENCE_MS = 800;
export const PHRASE_MAX_MS = 15_000;
export const PHRASE_PREROLL_MS = 300;

/**
 * Cuts my microphone into phrases on silence, for an engine that transcribes whole clips (Voz). Feed it chunks of
 * samples with whether each was loud; it hands back a finished phrase (its samples and when it started, ms on the
 * caller's clock) once enough silence follows speech or the phrase gets too long.
 */
export function createPhraseCutter(sampleRate: number) {
  let pre: Float32Array[] = [];
  let preMs = 0;
  let phrase: Float32Array[] = [];
  let phraseMs = 0;
  let startedAt = 0;
  let quietMs = 0;
  const finish = () => {
    const n = phrase.reduce((s, c) => s + c.length, 0);
    const samples = new Float32Array(n);
    let o = 0;
    for (const c of phrase) {
      samples.set(c, o);
      o += c.length;
    }
    const out = { samples, startedAt };
    phrase = [];
    phraseMs = 0;
    quietMs = 0;
    return out;
  };
  return {
    push(chunk: Float32Array, loud: boolean, now: number): { samples: Float32Array; startedAt: number } | undefined {
      const ms = (chunk.length / sampleRate) * 1000;
      if (phrase.length === 0) {
        if (!loud) {
          pre.push(chunk);
          preMs += ms;
          while (pre.length > 1 && preMs - (pre[0]!.length / sampleRate) * 1000 >= PHRASE_PREROLL_MS) preMs -= (pre.shift()!.length / sampleRate) * 1000;
          return undefined;
        }
        phrase = [...pre, chunk];
        phraseMs = preMs + ms;
        startedAt = now - phraseMs;
        pre = [];
        preMs = 0;
        quietMs = 0;
        return undefined;
      }
      phrase.push(chunk);
      phraseMs += ms;
      quietMs = loud ? 0 : quietMs + ms;
      if (quietMs >= PHRASE_SILENCE_MS || phraseMs >= PHRASE_MAX_MS) return finish();
      return undefined;
    },
    /** The phrase in progress, if any (captions turned off mid-sentence). */
    flush(): { samples: Float32Array; startedAt: number } | undefined {
      return phrase.length === 0 ? undefined : finish();
    },
  };
}

type VozInstance = { transcribe(input: Float32Array, options?: { sampleRate?: number }): Promise<{ text: string; words: { text: string; start: number; end: number }[] }> };

export type CaptionerHost = {
  /** My processed microphone track (starts it if needed); undefined when there is none. */
  micTrack(): Promise<MediaStreamTrack | undefined>;
  /** My microphone level now (0–1). */
  level(): number;
  emit(chunk: SpeechChunk): void;
  /** What this device can do changed (model downloaded, a failure, the setting). */
  capabilityChanged(): void;
};

export type Captioner = {
  setMode(mode: HuddleTranscription | "off"): void;
  /** Undefined until the engines here have been measured. */
  capability(): TranscriptionCapability | undefined;
  /** The call ended: a new call starts with no failures counted. */
  endCall(): void;
};

export function createCaptioner(host: CaptionerHost): Captioner {
  let mode: HuddleTranscription | "off" = "off";
  let generation = 0;
  let recognition: Recognition | undefined;
  let recorder: MediaRecorder | undefined;
  let clipTimer: ReturnType<typeof setTimeout> | undefined;
  let levelTimer: ReturnType<typeof setInterval> | undefined;
  let audio: { ctx: AudioContext; stop(): void } | undefined;

  // What runs here, measured once (below).
  let measured = false;
  let engine: "voz" | "web-speech" | null = null;
  let freeStorage: number | undefined;
  let model: ModelState = { kind: "missing", bytes: VOZ_BYTES };
  let voz: VozInstance | undefined;
  let vozLoading: Promise<VozInstance | undefined> | undefined;
  let failures = 0;

  const deviceMemory = (globalThis.navigator as { deviceMemory?: number } | undefined)?.deviceMemory;

  const changed = () => host.capabilityChanged();

  void (async () => {
    try {
      const gpu = (navigator as { gpu?: { requestAdapter(): Promise<unknown> } }).gpu;
      if (gpu !== undefined && (await gpu.requestAdapter().catch(() => null)) !== null) {
        engine = "voz";
        const estimate = await navigator.storage?.estimate?.().catch(() => undefined);
        if (estimate?.quota !== undefined) freeStorage = estimate.quota - (estimate.usage ?? 0);
      } else {
        const Ctor = recognitionCtor();
        const lang = captionLocale();
        const status = Ctor?.available === undefined ? "unavailable" : await Ctor.available({ langs: [lang], processLocally: true }).catch(() => "unavailable");
        if (status === "available") {
          engine = "web-speech";
          model = { kind: "system" };
        }
      }
    } finally {
      measured = true;
      changed();
    }
  })();
  window.addEventListener(ON_DEVICE_EVENT, changed);

  const device = () => ({
    settingOn: captionsOnDevice(),
    engine,
    languages: engine === "voz" ? VOZ_LANGUAGES : engine === "web-speech" ? [captionLocale()] : [],
    model,
    ...(freeStorage === undefined ? {} : { freeStorage }),
    ...(engine === "voz" && deviceMemory !== undefined ? { freeMemory: (deviceMemory * 1e9) / 2, memoryNeeded: VOZ_MEMORY } : {}),
    failures,
  });

  const failed = (err: unknown) => {
    console.warn("on-device captions failed", err);
    failures++;
    changed();
  };

  /** Starts fetching and compiling Voz when captions are wanted and nothing but the download blocks it. */
  const loadVoz = () => {
    if (engine !== "voz" || voz !== undefined || vozLoading !== undefined) return;
    const blocker = Transcription.deviceBlocker(device(), captionLocale());
    if (blocker !== "not_downloaded") return;
    model = { kind: "downloading", progress: 0 };
    changed();
    let shown = 0;
    vozLoading = import("@desert-ant-labs/voz")
      .then(({ Voz }) =>
        Voz.load({
          onProgress: (f: number) => {
            // A percent at a time: every progress event would re-render the call.
            if (Math.floor(f * 100) === shown) return;
            shown = Math.floor(f * 100);
            model = { kind: "downloading", progress: f };
            changed();
          },
        }),
      )
      .then((v: VozInstance) => {
        voz = v;
        model = { kind: "ready" };
        // Already captioning on the device (no server to fall back on while it downloaded): start listening now.
        if (mode === "client" && audio === undefined) void startVoz();
        changed();
        return v;
      })
      .catch((err: unknown) => {
        vozLoading = undefined;
        model = { kind: "missing", bytes: VOZ_BYTES };
        failed(err);
        return undefined;
      });
  };

  const stopAll = () => {
    generation++;
    if (recognition !== undefined) {
      recognition.onend = null;
      recognition.onresult = null;
      try {
        recognition.stop();
      } catch {
        // already stopped
      }
      recognition = undefined;
    }
    if (clipTimer !== undefined) clearTimeout(clipTimer);
    if (levelTimer !== undefined) clearInterval(levelTimer);
    clipTimer = undefined;
    levelTimer = undefined;
    if (recorder !== undefined && recorder.state !== "inactive") {
      recorder.ondataavailable = null;
      recorder.stop();
    }
    recorder = undefined;
    audio?.stop();
    audio = undefined;
  };

  /** Chrome / Edge / Safari's recognizer, on the device only. */
  const startRecognition = () => {
    const Ctor = recognitionCtor();
    if (Ctor === undefined) return;
    const gen = generation;
    const r = new Ctor();
    r.continuous = true;
    r.interimResults = false;
    r.processLocally = true;
    r.lang = captionLocale();
    r.onresult = (e) => {
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const res = e.results[i]!;
        const text = res[0].transcript.trim();
        if (res.isFinal && text !== "") host.emit({ kind: "text", text });
      }
    };
    // The recognizer stops by itself after a while: start again while captions are wanted.
    r.onend = () => {
      if (gen === generation && mode === "client") setTimeout(() => gen === generation && startRecognition(), 250);
    };
    r.onerror = (e) => {
      if (gen === generation && e.error !== "no-speech" && e.error !== "aborted") failed(new Error(e.error));
    };
    recognition = r;
    try {
      r.start();
    } catch {
      // started twice
    }
  };

  /** Voz: my microphone at 16 kHz, cut into phrases on silence, each transcribed on the device. */
  const startVoz = async () => {
    const gen = generation;
    const track = await host.micTrack();
    const v = voz;
    if (track === undefined || v === undefined || gen !== generation) return;
    const rate = 16_000;
    const ctx = new AudioContext({ sampleRate: rate });
    const source = ctx.createMediaStreamSource(new MediaStream([track]));
    // ScriptProcessor keeps this to one file (no worklet module to serve); 4096 samples is 256 ms at 16 kHz.
    const node = ctx.createScriptProcessor(4096, 1, 1);
    const cutter = createPhraseCutter(rate);
    let queue = Promise.resolve();
    const transcribe = (p: { samples: Float32Array; startedAt: number }) => {
      queue = queue.then(async () => {
        if (gen !== generation) return;
        try {
          const r = await v.transcribe(p.samples, { sampleRate: rate });
          if (gen !== generation) return;
          const words = Transcription.captionWords(r.words, 0);
          const req = Transcription.captionRequest(r.text, words, p.startedAt, Date.now());
          if (req !== null) host.emit({ kind: "text", text: req.text, ...(req.words === undefined ? {} : { words: req.words }), ...(req.ageMs === undefined ? {} : { ageMs: req.ageMs }) });
        } catch (err) {
          failed(err);
        }
      });
    };
    node.onaudioprocess = (e) => {
      const chunk = new Float32Array(e.inputBuffer.getChannelData(0));
      const phrase = cutter.push(chunk, Huddles.clipHasSpeech(host.level()), Date.now());
      if (phrase !== undefined) transcribe(phrase);
    };
    source.connect(node);
    // A ScriptProcessor only runs while connected to the output; it writes silence.
    node.connect(ctx.destination);
    audio = {
      ctx,
      stop() {
        node.onaudioprocess = null;
        const last = cutter.flush();
        if (last !== undefined) transcribe(last);
        source.disconnect();
        node.disconnect();
        void ctx.close();
      },
    };
  };

  const startClips = async () => {
    const gen = generation;
    const track = await host.micTrack();
    if (track === undefined || gen !== generation || typeof MediaRecorder === "undefined") return;
    const mimeType = CLIP_TYPES.find((t) => MediaRecorder.isTypeSupported(t)) ?? "";
    const stream = new MediaStream([track]);
    const clip = () => {
      if (gen !== generation) return;
      let peak = 0;
      levelTimer = setInterval(() => {
        peak = Math.max(peak, host.level());
      }, 100);
      const rec = new MediaRecorder(stream, mimeType === "" ? {} : { mimeType });
      recorder = rec;
      const parts: Blob[] = [];
      rec.ondataavailable = (e) => {
        if (e.data.size > 0) parts.push(e.data);
      };
      rec.onstop = () => {
        if (levelTimer !== undefined) clearInterval(levelTimer);
        levelTimer = undefined;
        if (gen !== generation) return;
        const type = (rec.mimeType || mimeType || "audio/webm").split(";")[0]!;
        if (parts.length > 0 && Huddles.clipHasSpeech(peak)) host.emit({ kind: "audio", clip: new Blob(parts, { type }), mime: type });
        clip();
      };
      rec.start();
      clipTimer = setTimeout(() => {
        if (rec.state !== "inactive") rec.stop();
      }, Huddles.CAPTION_CLIP_MS);
    };
    clip();
  };

  return {
    setMode(next) {
      if (next === mode) return;
      stopAll();
      mode = next;
      if (next !== "off") loadVoz();
      if (next === "client") {
        if (engine === "voz") void startVoz();
        else startRecognition();
      } else if (next === "server") void startClips();
    },
    capability: () => (measured ? { device: device(), locale: captionLocale() } : undefined),
    endCall() {
      this.setMode("off");
      if (failures === 0) return;
      failures = 0;
      changed();
    },
  };
}
